/**
 * Detects when a new service worker has installed and taken control,
 * exposing a callback so the UI can show an "Update available — refresh"
 * banner instead of forcing operators to manually force-quit the PWA.
 *
 * Why this is needed: scripts/version-sw.js stamps the build SHA into
 * sw.js so each deploy produces a different SW. Cloudflare Pages serves
 * sw.js with Cache-Control: no-cache. But iOS PWAs are conservative
 * about checking for SW updates — backgrounded tabs can run stale code
 * for hours unless we explicitly nudge the browser to check on focus.
 *
 * Two mechanisms:
 *   1. controllerchange listener — fires when a new SW takes over
 *   2. registration.update() on visibilitychange — forces an iOS-friendly
 *      check whenever the user switches back to the PWA
 */

export type UpdateCallback = () => void

let initialized = false
let listeners: UpdateCallback[] = []

export function watchForUpdates(): void {
  if (initialized) return
  initialized = true

  if (!('serviceWorker' in navigator)) return

  // First-time SW registration on a fresh visit also fires controllerchange,
  // but that's a "controller assigned", not an "update". We only signal for
  // updates that happen after the page already had a controller on boot.
  const hadControllerOnBoot = navigator.serviceWorker.controller !== null

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadControllerOnBoot) return
    for (const fn of listeners) {
      try {
        fn()
      } catch (err) {
        console.error('SW update listener threw:', err)
      }
    }
  })

  // iOS-PWA workaround: backgrounded PWAs don't auto-poll sw.js. Force a
  // check whenever the tab becomes visible. If a new SW is found, install
  // + activate fires, controllerchange fires, banner appears.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    void navigator.serviceWorker
      .getRegistration()
      .then((reg) => reg?.update().catch(() => {}))
  })
}

export function onUpdateAvailable(fn: UpdateCallback): () => void {
  listeners.push(fn)
  return () => {
    listeners = listeners.filter((l) => l !== fn)
  }
}
